import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { CareerService } from './career.service';
import { Career, AIRecommendation, CareerRound } from '../../interfaces/career/career.interface';
import { environment } from '../../../../../environments/environment.dev';
import { CareerSearchParam } from '../../interfaces/career/career-search-param.interface';
import { convertToHttpParams } from '../../../../core/interfaces/common-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class CareerApiService extends CareerService {
    private http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiBaseUrl}v1/admin-careers`;
    private readonly batchBaseUrl = `${environment.apiBaseUrl}v1/batch/batch-carrer`;

    constructor() {
        super();
    }

    override findBatchCareersAll(searchParam: CareerSearchParam): Observable<PageResponse<Career>> {
        return this.wrapRequest(
            this.http.get<any>(`${this.batchBaseUrl}/${searchParam.batchUniqueId}/${searchParam.institutionUniqueId}`, { headers: this.headers, params: convertToHttpParams(searchParam) }).pipe(
                map((response: any) => {
                    const data = response.response || response;
                    if (data.content) {
                        data.content = data.content.map((c: any) => this.mapFromApi(c));
                    }
                    return data as PageResponse<Career>;
                })
            )
        );
    }

    override findAll(searchParam: CareerSearchParam): Observable<PageResponse<Career>> {
        return this.wrapRequest(
            this.http.get<any>(this.baseUrl, { headers: this.headers, params: convertToHttpParams(searchParam) }).pipe(
                map((response: any) => {
                    const data = response.response || response;
                    if (data.content) {
                        data.content = data.content.map((c: any) => this.mapFromApi(c));
                    }
                    return data as PageResponse<Career>;
                })
            )
        );
    }

    override find(id: string): Observable<Career> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
                map((response: any) => this.mapFromApi(response.response || response))
            )
        );
    }

    override create(data: Partial<Career>): Observable<Career> {
        return this.wrapRequest(
            this.http.post<any>(this.baseUrl, data, { headers: this.headers }).pipe(
                map((response: any) => this.mapFromApi(response.response || response))
            )
        );
    }

    override update(id: string, data: Partial<Career>): Observable<Career> {
        return this.wrapRequest(
            this.http.put<any>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
                map((response: any) => this.mapFromApi(response.response || response))
            )
        );
    }

    private mapFromApi(data: any): Career {
        return {
            ...data,
            careerUniqueId: data.careerUniqueId || data.uniqueId,
            title: data.title || data.careerName || data.roleName || data.careerUniqueId,
            // Ensure status is handled correctly if null
            status: data.status || data.purchaseStatus || 'EXPLORING_OPPORTUNITIES',
            // Normalize availability type if singular
            availabilityType: data.availabilityType === 'SPECIFIC_INSTITUTION' ? 'SPECIFIC_INSTITUTIONS' : data.availabilityType,
            aiSuggestionSkills: data.aiSuggestionSkills || []
        };
    }

    override delete(id: string, institutionUniqueId?: string): Observable<void> {
        const url = institutionUniqueId ? `${this.baseUrl}/${id}/institutions/${institutionUniqueId}` : `${this.baseUrl}/${id}`;
        return this.wrapRequest(
            this.http.delete<void>(url, { headers: this.headers })
        );
    }

    override getTrendingCareers(): Observable<Career[]> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/trending`, { headers: this.headers }).pipe(
                map((response: any) => (response.response || response).map((c: any) => this.mapFromApi(c)))
            )
        );
    }

    override getAIRecommendation(): Observable<AIRecommendation> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/ai-recommendation`, { headers: this.headers }).pipe(
                map((response: any) => response.response || response)
            )
        );
    }

    override getRounds(careerUniqueId: string): Observable<CareerRound[]> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/${careerUniqueId}/rounds`, { headers: this.headers }).pipe(
                map((response: any) => response.response || response)
            )
        );
    }

    override createRound(careerUniqueId: string, data: Partial<CareerRound>): Observable<CareerRound> {
        return this.wrapRequest(
            this.http.post<any>(`${this.baseUrl}/${careerUniqueId}/rounds`, data, { headers: this.headers }).pipe(
                map((response: any) => response.response || response)
            )
        );
    }

    override updateRound(careerUniqueId: string, roundUniqueId: string, data: Partial<CareerRound>): Observable<CareerRound> {
        return this.wrapRequest(
            this.http.put<any>(`${this.baseUrl}/${careerUniqueId}/rounds/${roundUniqueId}`, data, { headers: this.headers }).pipe(
                map((response: any) => response.response || response)
            )
        );
    }

    override assignInstitution(careerUniqueId: string, institutionUniqueId: string): Observable<void> {
        return this.wrapRequest(
            this.http.post<void>(`${this.baseUrl}/${careerUniqueId}/institutions/${institutionUniqueId}`, {}, { headers: this.headers })
        );
    }
}

